import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Siren, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState } from "@/components/brand";
import { Button } from "@/components/ui/button";

export const SEV = { minor: "bg-health/15 text-health", moderate: "bg-[#FFA000]/15 text-[#B87300]", major: "bg-[#F2611D]/15 text-[#C2410C]", critical: "bg-alert/15 text-alert" };
export const IST = { reported: "Reported", under_investigation: "Under investigation", actions_in_progress: "Actions in progress", closed: "Closed" };
export const TYPES = { accident: "Accident", near_miss: "Near miss", unsafe_act: "Unsafe act", unsafe_condition: "Unsafe condition", environmental: "Environmental", dangerous_occurrence: "Dangerous occurrence", property_damage: "Property damage", other: "Other" };
export const Pill = ({ c, children, testid }) => <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${c}`} data-testid={testid}>{children}</span>;
const MANAGERS = ["owner", "admin", "hse_manager", "supervisor", "project_manager"];

const Stats = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get("/incidents/stats").then((r) => setS(r.data)).catch(() => {});
  }, []);
  if (!s) return null;
  const max = Math.max(1, ...Object.values(s.by_month));
  return (
    <div className="grid gap-4 lg:grid-cols-3" data-testid="incident-stats">
      <div className="grid grid-cols-2 gap-3">{[["Total", s.total], ["Open", s.open], ["Open actions", s.open_actions], ["Overdue actions", s.overdue_actions]].map(([l, v]) => (
        <div key={l} className="rounded-2xl border bg-card p-4"><p className="text-xs text-muted-foreground">{l}</p><p className={`mt-1 font-display text-2xl font-bold ${l === "Overdue actions" && v ? "text-alert" : ""}`}>{v}</p></div>))}</div>
      <div className="rounded-2xl border bg-card p-4"><p className="mb-3 text-xs text-muted-foreground">By type</p>
        <ul className="space-y-1.5 text-sm">{Object.entries(s.by_type).map(([k, v]) => <li key={k} className="flex justify-between"><span>{TYPES[k]}</span><span className="font-mono">{v}</span></li>)}{!s.total && <li className="text-muted-foreground">No reports yet</li>}</ul></div>
      <div className="rounded-2xl border bg-card p-4"><p className="mb-3 text-xs text-muted-foreground">Trend (by month of occurrence)</p>
        <div className="flex h-28 items-end gap-2">{Object.entries(s.by_month).map(([m, v]) => <div key={m} className="flex flex-1 flex-col items-center gap-1"><div className="w-full rounded-t bg-health/70" style={{ height: `${(v / max) * 90}px` }} /><span className="font-mono text-[9px] text-muted-foreground">{m.slice(5)}</span></div>)}</div></div>
    </div>
  );
};

export default function Incidents() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/incidents").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);
  if (!list) return <Loader />;
  const manager = MANAGERS.includes(user.role);
  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="incidents-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><p className="eyebrow">Incidents & near misses</p><h1 className="mt-2 text-3xl font-bold">Incident reporting</h1>
          <p className="text-sm text-muted-foreground">{manager ? "Every report across your organisation, with investigations and corrective actions." : "Reports you have made."}</p></div>
        <Button asChild size="lg" className="rounded-full"><Link to="/app/incidents/new" data-testid="report-incident-btn"><Plus className="mr-1.5 h-4 w-4" />Report an incident</Link></Button>
      </div>
      {manager && <Stats />}
      {list.length === 0 ? <EmptyState icon={Siren} title="No incidents reported" text="Report accidents, near misses and unsafe conditions from any phone in under a minute." /> : (
        <div className="space-y-2">{list.map((i) => (
          <Link key={i.id} to={`/app/incidents/${i.id}`} data-testid={`incident-row-${i.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-4 transition-colors hover:bg-accent/50">
            <span className="font-mono text-xs text-muted-foreground">{i.ref}</span>
            <div className="min-w-0 flex-1"><p className="truncate font-medium">{i.title}</p><p className="text-xs text-muted-foreground">{TYPES[i.type]} · {i.location || "No location"} · {new Date(i.occurred_at).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" })}</p></div>
            <Pill c={SEV[i.severity]}>{i.severity}</Pill><Pill c="bg-muted">{IST[i.status]}</Pill>
            {i.actions?.some((a) => a.status === "open") && <span className="text-xs text-[#B87300]">{i.actions.filter((a) => a.status === "open").length} open action(s)</span>}
          </Link>
        ))}</div>
      )}
    </div>
  );
}
